<script setup lang="ts">
import { logoBarSchema } from '@/content/logoBar'

useBlockFormSchema(logoBarSchema)

const { t } = useI18n({ useScope: 'local' })

const { fields, push, remove } = useFieldArray('logos')

function addLogo() {
  push({
    image: null,
    altText: '',
    url: null,
    smallSize: false,
  })
}
</script>

<template>
  <div class="flex flex-col gap-y-4 pt-4">
    <BaseParagraph class="mt-0">
      {{ t('explanation') }}
    </BaseParagraph>

    <div class="flex flex-col gap-y-4">
      <BaseHeading as="h3" class="text-lg font-bold">
        {{ t('logosLabel') }}
      </BaseHeading>

      <FormCheckboxField
        name="grayscale"
        :label="t('grayscaleLabel')"
      />

      <FormField
        name="defaultMaxWidth"
        :label="t('defaultMaxWidthLabel')"
        :placeholder="t('defaultMaxWidthPlaceholder')"
        type="number"
      />

      <div
        v-for="(logo, index) in fields"
        :key="index"
        class="border-primary-500 relative flex flex-col gap-y-3 rounded-xl border p-4"
      >
        <div class="flex items-center justify-between">
          <BaseHeading as="h4" class="font-medium">
            {{ t('logoCount', { count: index + 1 }) }}
          </BaseHeading>
          <BaseButtonIcon
            color="danger"
            shape="curved"
            muted
            class="scale-75"
            @click="remove(index)"
          >
            <Icon name="ph:trash" class="size-5" />
          </BaseButtonIcon>
        </div>

        <FormImageField
          :name="`logos[${index}].image`"
          :label="t('logoLabel')"
          keep-value
        />

        <FormField
          :name="`logos[${index}].altText`"
          :label="t('altTextLabel')"
        />

        <FormField
          :name="`logos[${index}].url`"
          :label="t('urlLabel')"
          :placeholder="t('urlPlaceholder')"
        />

        <FormField
          :name="`logos[${index}].maxWidth`"
          :label="t('maxWidthLabel')"
          :placeholder="t('maxWidthPlaceholder')"
          type="number"
        />
      </div>
    </div>

    
    <BaseButton @click="addLogo">
      {{ t('addButton') }}
    </BaseButton>
  </div>
</template>

<i18n lang="json">
{
  "en": {
    "explanation": "Upload multiple logos to create a horizontal logo bar.",
    "logosLabel": "Logos",
    "logoLabel": "Logo",
    "logoCount": "Logo {count}",
    "addButton": "Add extra logo",
    "grayscaleLabel": "Apply grayscale",
    "altTextLabel": "Alt text",
    "altTextHelp": "Alternative text for the logo (important for accessibility and SEO)",
    "urlLabel": "URL",
    "urlPlaceholder": "https://example.com",
    "maxWidthLabel": "Max width",
    "maxWidthPlaceholder": "Example: 50",
    "defaultMaxWidthLabel": "Default max width",
    "defaultMaxWidthPlaceholder": "Example: 50",
  },
  "nl": {
    "explanation": "Upload meerdere logo's om een horizontale logo balk te maken.",
    "logosLabel": "Logo's",
    "logoLabel": "Logo",
    "logoCount": "Logo {count}",
    "addButton": "Extra logo toevoegen",
    "grayscaleLabel": "Grijstinten toepassen",
    "altTextLabel": "Alt tekst",
    "urlLabel": "URL",
    "urlPlaceholder": "https://voorbeeld.nl",
    "maxWidthLabel": "Max breedte in pixels (optioneel)",
    "maxWidthPlaceholder": "Bijvoorbeeld: 50",
    "defaultMaxWidthLabel": "Standaard max breedte",
    "defaultMaxWidthPlaceholder": "Bijvoorbeeld: 50"
  }
}
</i18n>
